import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export type Period = 'day' | 'week' | 'month' | 'year' | 'custom';

export const PERIOD_LABELS: Record<Period, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
  custom: 'Custom',
};

export function weekStart(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const r = new Date(d);
  r.setDate(d.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

export function rangeLabel(
  period: Period,
  ref: Date,
  customFrom?: string,
  customTo?: string,
): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  switch (period) {
    case 'day':
      return ref.toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
    case 'week': {
      const ws = weekStart(ref);
      const we = new Date(ws);
      we.setDate(ws.getDate() + 6);
      return `${ws.toLocaleDateString('en-GB', opts)} – ${we.toLocaleDateString('en-GB', opts)}`;
    }
    case 'month':
      return ref.toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric',
      });
    case 'year':
      return String(ref.getFullYear());
    case 'custom': {
      if (customFrom && customTo) {
        const fmtDate = (iso: string) =>
          new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
        return `${fmtDate(customFrom)} – ${fmtDate(customTo)}`;
      }
      return 'Custom range';
    }
  }
}

export function shiftDate(period: Period, ref: Date, dir: -1 | 1): Date {
  const d = new Date(ref);
  switch (period) {
    case 'day':
      d.setDate(d.getDate() + dir);
      break;
    case 'week':
      d.setDate(d.getDate() + dir * 7);
      break;
    case 'month':
      d.setMonth(d.getMonth() + dir);
      break;
    case 'year':
      d.setFullYear(d.getFullYear() + dir);
      break;
    case 'custom':
      /* no prev/next navigation */ break;
  }
  return d;
}

export function isFutureRef(period: Period, ref: Date, now: Date): boolean {
  switch (period) {
    case 'day':
      return ref > now;
    case 'week':
      return weekStart(ref) > weekStart(now);
    case 'month':
      return (
        ref.getFullYear() > now.getFullYear() ||
        (ref.getFullYear() === now.getFullYear() &&
          ref.getMonth() > now.getMonth())
      );
    case 'year':
      return ref.getFullYear() > now.getFullYear();
    case 'custom':
      return false;
  }
}

export interface PeriodSelectorProps {
  period: Period;
  refDate: Date;
  now: Date;
  onPeriodChange: (p: Period) => void;
  onPrev: () => void;
  onNext: () => void;
  customFrom?: string;
  customTo?: string;
  onCustomApply?: (from: string, to: string) => void;
}

const WASH = '#f4f7fb';
const INK = '#17263d';
const MUTED = '#78869a';
const BLUE = '#1475d1';

const todayISO = () => new Date().toISOString().slice(0, 10);

const PeriodSelector = ({
  period,
  refDate,
  now,
  onPeriodChange,
  onPrev,
  onNext,
  customFrom,
  customTo,
  onCustomApply,
}: PeriodSelectorProps) => {
  const [draftFrom, setDraftFrom] = useState(customFrom ?? '');
  const [draftTo, setDraftTo] = useState(customTo ?? '');
  const [pickerErr, setPickerErr] = useState('');

  useEffect(() => {
    setDraftFrom(customFrom ?? '');
  }, [customFrom]);
  useEffect(() => {
    setDraftTo(customTo ?? '');
  }, [customTo]);

  const isCustom = period === 'custom';
  const nextRef = shiftDate(period, refDate, 1);
  const nextFuture = isFutureRef(period, nextRef, now);

  const handleTabClick = (p: Period) => {
    if (p !== 'custom') setPickerErr('');
    onPeriodChange(p);
  };

  const handleApply = () => {
    if (!draftFrom || !draftTo) {
      setPickerErr('Both From and To dates are required.');
      return;
    }
    if (draftTo < draftFrom) {
      setPickerErr('"To" must not be earlier than "From".');
      return;
    }
    setPickerErr('');
    onCustomApply?.(draftFrom, draftTo);
  };

  const handleCancel = () => {
    setDraftFrom('');
    setDraftTo('');
    setPickerErr('');
    onPeriodChange('month');
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        {/* Pill tabs */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: WASH,
            borderRadius: '10px',
            p: '3px',
            gap: '2px',
          }}
        >
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <Box
              key={p}
              onClick={() => handleTabClick(p)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleTabClick(p)}
              sx={{
                px: '14px',
                py: '5px',
                borderRadius: '8px',
                cursor: 'pointer',
                bgcolor: period === p ? '#fff' : 'transparent',
                boxShadow:
                  period === p ? '0 1px 4px rgba(22,54,93,0.1)' : 'none',
                transition: 'all 0.15s',
                userSelect: 'none',
                '&:focus-visible': { outline: `2px solid ${BLUE}` },
              }}
            >
              <Typography
                variant="caption"
                fontWeight={period === p ? 700 : 500}
                sx={{ color: period === p ? INK : MUTED }}
              >
                {PERIOD_LABELS[p]}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Range navigation — hidden when custom period active */}
        {!isCustom && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              ml: 'auto',
            }}
          >
            <IconButton
              size="small"
              onClick={onPrev}
              aria-label="Previous period"
              sx={{ color: MUTED, '&:hover': { color: INK } }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: INK, minWidth: 148, textAlign: 'center' }}
            >
              {rangeLabel(period, refDate)}
            </Typography>
            <IconButton
              size="small"
              onClick={onNext}
              disabled={nextFuture}
              aria-label="Next period"
              sx={{ color: MUTED, '&:hover': { color: INK } }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Applied custom range label */}
        {isCustom && customFrom && customTo && (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: INK, ml: 'auto' }}
          >
            {rangeLabel('custom', refDate, customFrom, customTo)}
          </Typography>
        )}
      </Box>

      {/* Custom date picker — shown when custom tab is active */}
      {isCustom && (
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={draftFrom}
            onChange={(e) => {
              setDraftFrom(e.target.value);
              setPickerErr('');
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: todayISO() }}
            error={!!pickerErr && !draftFrom}
            sx={{ width: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={draftTo}
            onChange={(e) => {
              setDraftTo(e.target.value);
              setPickerErr('');
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: todayISO() }}
            error={
              !!pickerErr && (!draftTo || (!!draftFrom && draftTo < draftFrom))
            }
            sx={{ width: 160 }}
          />
          <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Box>
          {pickerErr && (
            <Typography
              variant="caption"
              color="error"
              sx={{ flexBasis: '100%', mt: -1 }}
            >
              {pickerErr}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PeriodSelector;
